import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import TripRevealHero from '../assets/img/trip-reveal-special-message-jack-andrew.jpeg';
import { GET_BASE_URL } from '../constants/apiEndpoints';
import { loadStripeJs, type StripeEmbeddedCheckout } from '../utils/stripeEmbeddedCheckout';

export const TRIP_REVEAL_VIDEO_PATH = '/disneyland/personalized-disney-trip-reveal-video';

export type TripRevealVideoForm = {
  customer_name: string;
  customer_email: string;
  kids_names: string;
  kids_ages: string;
  destination: string;
  trip_timing: string;
  surprise_from: string;
  favorites: string;
  inside_touches: string;
  showing_format: string;
  reveal_date: string;
  personal_message: string;
};

const initialForm: TripRevealVideoForm = {
  customer_name: '',
  customer_email: '',
  kids_names: '',
  kids_ages: '',
  destination: 'Disneyland',
  trip_timing: '',
  surprise_from: '',
  favorites: '',
  inside_touches: '',
  showing_format: 'TV / widescreen',
  reveal_date: '',
  personal_message: '',
};

const detailList = [
  'Kids names, nicknames, and pronunciation notes',
  'Ages, destination, and trip timing',
  'Who is giving the surprise and who is going',
  'Favorite characters, rides, movies, and inside touches',
  'How you will show it, so we deliver the right format',
  'Your reveal date, so it is ready before the moment',
];

const TripRevealVideo = () => {
  const stripeCheckoutRef = useRef<StripeEmbeddedCheckout | null>(null);
  const stripeCheckoutContainerRef = useRef<HTMLDivElement | null>(null);
  const [form, setForm] = useState<TripRevealVideoForm>(initialForm);
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [checkoutMessage, setCheckoutMessage] = useState('');

  const destroyStripeCheckout = useCallback(() => {
    if (stripeCheckoutRef.current) {
      stripeCheckoutRef.current.destroy();
      stripeCheckoutRef.current = null;
    }
  }, []);

  const updateField = (
    field: keyof TripRevealVideoForm,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const startCheckout = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsStartingCheckout(true);
    setCheckoutError('');
    setCheckoutMessage('');
    destroyStripeCheckout();

    try {
      const response = await axios.post(`${GET_BASE_URL}/api/v1/stripe/trip-reveal-video/checkout-session`, {
        ...form,
        return_url: `${window.location.origin}${TRIP_REVEAL_VIDEO_PATH}`,
      });

      const { publishableKey, clientSecret } = response.data || {};
      if (!publishableKey || !clientSecret) {
        throw new Error('Stripe checkout did not return a usable session.');
      }

      await loadStripeJs();
      if (!window.Stripe) {
        throw new Error('Stripe.js did not load.');
      }

      const stripe = window.Stripe(publishableKey);
      const checkout = await stripe.initEmbeddedCheckout({
        fetchClientSecret: async () => clientSecret,
        onComplete: () => {
          setCheckoutMessage('Payment complete. We received your reveal details and will follow up by email.');
          destroyStripeCheckout();
        },
      });

      stripeCheckoutRef.current = checkout;
      if (stripeCheckoutContainerRef.current) {
        checkout.mount(stripeCheckoutContainerRef.current);
        if (typeof stripeCheckoutContainerRef.current.scrollIntoView === 'function') {
          stripeCheckoutContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    } catch (error) {
      console.error('Error starting trip reveal video checkout:', error);
      setCheckoutError(error instanceof Error ? error.message : 'Unable to start Stripe checkout.');
    } finally {
      setIsStartingCheckout(false);
    }
  };

  useEffect(() => {
    return () => {
      destroyStripeCheckout();
    };
  }, [destroyStripeCheckout]);

  return (
    <>
      <Helmet>
        <title>Personalized Disney Trip Reveal Video | Custom MouseWait Vacation Reveal</title>
        <meta
          name="description"
          content="Create a custom trip reveal video for your Disneyland, Walt Disney World, or Disney cruise surprise. Personalized with names, date, destination, and message. Fan-made MouseWait keepsake, delivered as an MP4."
        />
        <link rel="canonical" href={`https://mousewait.com${TRIP_REVEAL_VIDEO_PATH}`} />
      </Helmet>

      <main className="mw-trip-reveal-video" aria-labelledby="trip-reveal-video-title">
        <section className="mw-trip-reveal-video__hero">
          <div className="mw-trip-reveal-video__copy">
            <p className="mw-trip-reveal-video__eyebrow">Personalized MouseWait Trip Reveal Video</p>
            <h1 id="trip-reveal-video-title">Personalized Disney Trip Reveal Video</h1>
            <p className="mw-trip-reveal-video__lead">
              The Big Reveal is a custom Disney surprise video built around your family, your trip,
              and the exact moment you finally say, "we are going to Disney."
            </p>
            <div className="mw-trip-reveal-video__price" aria-label="Price">$20</div>
            <a className="mw-trip-reveal-video__jump" href="#trip-reveal-order">
              Start my reveal video
            </a>
          </div>
          <div className="mw-trip-reveal-video__preview" aria-label="Trip reveal video preview">
            <div className="mw-trip-reveal-video__screen">
              <img
                src={TripRevealHero}
                alt="Sample Disney trip reveal video title card with a castle and a special message"
              />
              <span className="mw-trip-reveal-video__play" aria-hidden="true" />
            </div>
          </div>
        </section>

        <section className="mw-trip-reveal-video__story">
          <h2>The Big Reveal - A Custom Disney Surprise Video</h2>
          <p>
            You know that moment. The one where you finally tell the kids we are going to Disney,
            and their faces light up. The screaming, the happy tears, and the replay-it-forever
            reaction video the whole family watches a hundred times.
          </p>
          <p>
            We make that moment bigger. You play it for your kids or grandkids, hit record on their
            reaction, and watch the magic happen. It builds suspense, drops the news - Disneyland,
            Walt Disney World, or a Disney Cruise - and lands the kind of jaw-on-the-floor moment
            that is worth the whole trip on its own.
          </p>
          <p>
            It is not a template with your name slapped on it. We write it around your kids, their
            favorite characters, the trip you are actually taking, and who is surprising them. If
            Grandma and Grandpa are making it happen, the video says so. That is usually the part
            that gets everyone misty.
          </p>
        </section>

        <section className="mw-trip-reveal-video__details" aria-label="What we need">
          <div>
            <h2>What we need</h2>
            <ul>
              {detailList.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
          <div>
            <h2>How to use it</h2>
            <p>
              Show it on the TV at a birthday party, play it on your phone at the dinner table, or
              project it on the wall on Christmas morning. We deliver the finished fan-made
              MouseWait keepsake as an MP4.
            </p>
          </div>
        </section>

        <section id="trip-reveal-order" className="mw-trip-reveal-video__order">
          <div className="mw-trip-reveal-video__order-intro">
            <h2>Order your custom reveal</h2>
            <p>
              Send us the details below and we will handle the rest. If we need anything else, we
              will follow up by email after checkout.
            </p>
          </div>

          <form className="mw-trip-reveal-video__form" onSubmit={startCheckout}>
            <label>
              Your name
              <input
                required
                value={form.customer_name}
                onChange={(event) => updateField('customer_name', event)}
                placeholder="Kelly"
              />
            </label>
            <label>
              Email for delivery
              <input
                required
                type="email"
                value={form.customer_email}
                onChange={(event) => updateField('customer_email', event)}
                placeholder="you@example.com"
              />
            </label>
            <label className="mw-trip-reveal-video__wide">
              Kids names, spellings, nicknames, and pronunciation
              <textarea
                required
                rows={4}
                value={form.kids_names}
                onChange={(event) => updateField('kids_names', event)}
                placeholder="Ava, age 8, pronounced AY-vuh; Max, age 5"
              />
            </label>
            <label>
              Ages
              <input
                value={form.kids_ages}
                onChange={(event) => updateField('kids_ages', event)}
                placeholder="8 and 5"
              />
            </label>
            <label>
              Destination
              <select
                required
                value={form.destination}
                onChange={(event) => updateField('destination', event)}
              >
                <option>Disneyland</option>
                <option>Walt Disney World</option>
                <option>Disney Cruise</option>
                <option>Other Disney trip</option>
              </select>
            </label>
            <label>
              When you are going
              <input
                value={form.trip_timing}
                onChange={(event) => updateField('trip_timing', event)}
                placeholder="July 12, this summer, or soon"
              />
            </label>
            <label>
              Who is giving the surprise and who is coming
              <input
                value={form.surprise_from}
                onChange={(event) => updateField('surprise_from', event)}
                placeholder="From Grandma and Grandpa"
              />
            </label>
            <label className="mw-trip-reveal-video__wide">
              What the kids love
              <textarea
                rows={3}
                value={form.favorites}
                onChange={(event) => updateField('favorites', event)}
                placeholder="Favorite characters, rides, movies, snacks, or songs"
              />
            </label>
            <label className="mw-trip-reveal-video__wide">
              Inside touches
              <textarea
                rows={3}
                value={form.inside_touches}
                onChange={(event) => updateField('inside_touches', event)}
                placeholder="Family catchphrases, nicknames, or inside jokes"
              />
            </label>
            <label>
              How you will show it
              <select
                required
                value={form.showing_format}
                onChange={(event) => updateField('showing_format', event)}
              >
                <option>TV / widescreen</option>
                <option>Phone / vertical</option>
                <option>Projector / widescreen</option>
                <option>Not sure yet</option>
              </select>
            </label>
            <label>
              Reveal date
              <input
                value={form.reveal_date}
                onChange={(event) => updateField('reveal_date', event)}
                placeholder="Need it by December 20"
              />
            </label>
            <label className="mw-trip-reveal-video__wide">
              Personal message
              <textarea
                rows={3}
                value={form.personal_message}
                onChange={(event) => updateField('personal_message', event)}
                placeholder="Anything specific you want the reveal to say"
              />
            </label>

            {checkoutError ? (
              <p className="mw-trip-reveal-video__error" role="alert">
                {checkoutError}
              </p>
            ) : null}
            {checkoutMessage ? (
              <p className="mw-trip-reveal-video__success" role="status">
                {checkoutMessage}
              </p>
            ) : null}

            <button className="mw-trip-reveal-video__submit" type="submit" disabled={isStartingCheckout}>
              {isStartingCheckout ? 'Starting checkout...' : 'Buy with Stripe - $20'}
            </button>
          </form>

          <div
            className="mw-trip-reveal-video__checkout"
            ref={stripeCheckoutContainerRef}
            aria-label="Stripe checkout"
          />
        </section>
      </main>
    </>
  );
};

export default TripRevealVideo;
